import React, { useEffect, useState } from "react";
import "../../Styles/BankScreen.css";

import { apiPost } from "../../Api/Client.js";
import { Panel, SimpleTable } from "../../Shared/ScreenComponents.jsx";
import { canUpdate } from "../../Services/pageAccess.js";
import { accountStatus, pickEmployeeAndAccount } from "./bankSelection.js";
import { accountConfirmed, BANK_DETAILS_CONFIRMATION } from "./bankConfirm.js";

function formFromAccount(account) {
  return { ifsc: account.ifsc_code || "", account: account.metadata?.legacy_account_number || (account.masked_account_number && !account.masked_account_number.includes("*") ? account.masked_account_number : ""), confirm: "", upi: account.upi_id || "" };
}

export function BankDetailsScreen({ data, selectedEmployeeId, reload }) {
  const { employee, account } = pickEmployeeAndAccount(data, selectedEmployeeId);
  const storedAccount = formFromAccount(account).account;
  const [form, setForm] = useState(() => formFromAccount(account));
  const [saving, setSaving] = useState(false);
  const [vouched, setVouched] = useState(false);
  // { ok, text }: whether it worked is recorded, never guessed from the wording of the text.
  const [message, setMessage] = useState(null);

  useEffect(() => { setForm(formFromAccount(account)); setVouched(false); }, [account.id]);

  // Finance confirms new bank details before they are paid to; saving never marks them verified.
  const mayVerify = canUpdate(data?.me, "finance");
  const [verifying, setVerifying] = useState(null);
  const verify = async (accountId) => {
    setVerifying(accountId);
    setMessage(null);
    try {
      await apiPost(`/Users/EmployeeBankAccounts/${accountId}/verify/`, {});
      setMessage({ ok: true, text: "Bank details verified." });
      reload();
    } catch (err) {
      setMessage({ ok: false, text: err?.payload?.detail || "The bank details could not be verified." });
    } finally {
      setVerifying(null);
    }
  };

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await apiPost("/FinanceAndPayroll/Bankdetails/", { employee: selectedEmployeeId, Ac_No: form.account, Ac_IFSC: form.ifsc, upi: form.upi, details_confirmed: vouched });
      setMessage({ ok: true, text: "Bank Details Saved Successfully." });
      setVouched(false);
      reload();
    } catch (err) {
      setMessage({ ok: false, text: err?.payload?.detail || "Failed To Save Bank Details." });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="Form-Page" style={{ padding: "24px 28px" }}>
      <h1 style={{ marginBottom: 20, fontSize: 22, fontWeight: 700 }}>Bank Details</h1>
      <div style={{ background: "var(--surface)", border: "1px solid var(--c-200)", borderRadius: 12, padding: 20, maxWidth: 500 }}>
        <label style={{ display: "block", marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: "var(--c-500)", fontWeight: 600, textTransform: "uppercase" }}>Employee</span>
          <input value={employee.display_name || ""} readOnly style={{ width: "100%", padding: "8px 10px", marginTop: 4, border: "1px solid var(--c-200)", borderRadius: 6, fontSize: 14, background: "var(--surface-soft)" }} />
        </label>
        <label style={{ display: "block", marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: "var(--c-500)", fontWeight: 600, textTransform: "uppercase" }}>IFSC Code</span>
          <input value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value })} style={{ width: "100%", padding: "8px 10px", marginTop: 4, border: "1px Solid var(--c-200)", borderRadius: 6, fontSize: 14 }} />
        </label>
        <label style={{ display: "block", marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: "var(--c-500)", fontWeight: 600, textTransform: "uppercase" }}>Account Number</span>
          <input value={form.account} onChange={(e) => setForm({ ...form, account: e.target.value })} style={{ width: "100%", padding: "8px 10px", marginTop: 4, border: "1px Solid var(--c-200)", borderRadius: 6, fontSize: 14 }} />
        </label>
        <label style={{ display: "block", marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: "var(--c-500)", fontWeight: 600, textTransform: "uppercase" }}>Confirm Account Number</span>
          <input value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} placeholder="Re-Enter Account Number" style={{ width: "100%", padding: "8px 10px", marginTop: 4, border: "1px Solid var(--c-200)", borderRadius: 6, fontSize: 14 }} />
          {form.confirm && form.confirm !== form.account && <span style={{ fontSize: 11, color: "var(--red-500)" }}>Account Numbers Do Not Match</span>}
        </label>
        <label style={{ display: "block", marginBottom: 16 }}>
          <span style={{ fontSize: 12, color: "var(--c-500)", fontWeight: 600, textTransform: "uppercase" }}>UPI ID</span>
          <input value={form.upi} onChange={(e) => setForm({ ...form, upi: e.target.value })} style={{ width: "100%", padding: "8px 10px", marginTop: 4, border: "1px Solid var(--c-200)", borderRadius: 6, fontSize: 14 }} />
        </label>
        <label style={{ display: "flex", gap: 8, alignItems: "flex-start", marginBottom: 16, fontSize: 13, color: "var(--c-700)", cursor: "pointer" }}>
          <input type="checkbox" checked={vouched} onChange={(e) => setVouched(e.target.checked)} style={{ marginTop: 2, width: 16, height: 16, flex: "none" }} />
          <span>{BANK_DETAILS_CONFIRMATION}</span>
        </label>
        {message && <div style={{ fontSize: 13, padding: "8px 12px", borderRadius: 6, marginBottom: 12, background: message.ok ? "var(--green-50)" : "var(--red-50)", color: message.ok ? "var(--green-600)" : "var(--red-600)" }}>{message.text}</div>}
        <button className="Primary-Button" onClick={save} disabled={saving || !selectedEmployeeId || !vouched || !accountConfirmed(form, storedAccount)}>{saving ? "Saving..." : "Update Bank Details"}</button>
      </div>
      <Panel title="Current Bank Details">
        <SimpleTable
          columns={mayVerify ? ["Account Number", "IFSC Code", "UPI Address", "Status", ""] : ["Account Number", "IFSC Code", "UPI Address", "Status"]}
          rows={(data.bankAccounts || []).filter((item) => !selectedEmployeeId || String(item.employee) === String(selectedEmployeeId)).map((item) => {
            const cells = [item.masked_account_number, item.ifsc_code, item.upi_id, accountStatus(item)];
            if (!mayVerify) return cells;
            const canVerifyRow = !item.superseded_at && item.verification_status !== "Verified";
            return [...cells, canVerifyRow ? <button className="Primary-Button" onClick={() => verify(item.id)} disabled={verifying === item.id}>{verifying === item.id ? "Verifying..." : "Verify"}</button> : ""];
          })}
        />
      </Panel>
    </section>
  );
}